import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Text,
  RichText,
  LinkField,
  TextField,
  RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { clearUserLocks, unregisterActiveUser } from 'src/services/leadManagementApi';
import {
  LMA_ACTIVE_SESSION_EXPIRES_AT_KEY,
  LMA_AUTH_SESSION_KEY,
  LMA_CONCURRENT_EXEMPT_SESSION_KEY,
  LMA_IDLE_LOGOUT_SESSION_KEY,
  LMA_USER_REGISTERED_SESSION_KEY,
  LMA_USERNAME_LOCAL_KEY,
} from 'contexts/AuthContext';

const CDP_PROFILE_DATA_STORAGE_KEY = 'cdp-profile-data';

interface SessionEndedByAdminFields {
  Title?: TextField;
  Summary?: TextField;
  Description?: RichTextField;
  InfoText?: TextField;
  CTAButton?: LinkField;
}

type SessionEndedByAdminProps = ComponentProps & {
  fields: SessionEndedByAdminFields;
};

/**
 * Dedicated LMA page when an administrator removes the user’s concurrent session (kick block).
 * Add a Sitecore route whose last segment matches NEXT_PUBLIC_LMA_SESSION_ENDED_BY_ADMIN_PATH (default: session-ended-by-admin).
 */
const SessionEndedByAdmin = (props: SessionEndedByAdminProps): JSX.Element => {
  const { fields } = props;
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    if (typeof window === 'undefined') return;
    const userName = window.localStorage?.getItem(LMA_USERNAME_LOCAL_KEY);
    if (userName && process.env.NEXT_PUBLIC_LMA_LOCK_ENABLED === 'true') {
      clearUserLocks(userName).catch(() => undefined);
    }
    if (userName) {
      unregisterActiveUser(userName).catch(() => undefined);
    }
    sessionStorage.removeItem(LMA_USER_REGISTERED_SESSION_KEY);
    sessionStorage.removeItem(LMA_ACTIVE_SESSION_EXPIRES_AT_KEY);
    sessionStorage.removeItem(LMA_CONCURRENT_EXEMPT_SESSION_KEY);
    sessionStorage.removeItem(LMA_IDLE_LOGOUT_SESSION_KEY);
    sessionStorage.removeItem(CDP_PROFILE_DATA_STORAGE_KEY);
    sessionStorage.removeItem(LMA_AUTH_SESSION_KEY);
    if (window.localStorage) {
      window.localStorage.removeItem(LMA_USERNAME_LOCAL_KEY);
      window.localStorage.removeItem('lma-userId');
    }
  }, []);

  const handleGoBack = () => {
    const href = fields?.CTAButton?.value?.href;
    if (href) {
      window.location.href = href;
    }
  };

  if (!isMounted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #255E5E 0%, #1a4a4a 100%)',
        padding: 3,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 50 L45 65 L70 40' stroke='rgba(255,255,255,0.2)' stroke-width='4' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle cx='50' cy='50' r='35' stroke='rgba(255,255,255,0.15)' stroke-width='3' fill='none'/%3E%3C/svg%3E"),
            url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M25 40 L35 50 L55 30' stroke='rgba(255,255,255,0.15)' stroke-width='3' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3Ccircle cx='40' cy='40' r='28' stroke='rgba(255,255,255,0.1)' stroke-width='2' fill='none'/%3E%3C/svg%3E"),
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
          `,
          backgroundSize: '100px 100px, 80px 80px, 200px 200px, 200px 200px, 300px 300px',
          backgroundPosition: '0 0, 50px 50px, 0 0, 100% 100%, 50% 50%',
          animation: 'successPatternMove 20s linear infinite',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: 0.5,
        },
        '@keyframes successPatternMove': {
          '0%': {
            transform: 'translate(0, 0) rotate(0deg)',
          },
          '100%': {
            transform: 'translate(100px, 100px) rotate(360deg)',
          },
        },
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 4,
            backgroundColor: 'white',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: '#fff3e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '4px solid #ed6c02',
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: 60,
                  color: '#ed6c02',
                }}
              />
            </Box>
          </Box>

          {fields?.Title?.value ? (
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#212121',
                mb: 2,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                fontSize: {
                  xs: '1.75rem',
                  sm: '2rem',
                  md: '2.5rem',
                },
                lineHeight: 1.2,
              }}
            >
              <Text field={fields.Title} />
            </Typography>
          ) : (
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#212121',
                mb: 2,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                lineHeight: 1.2,
              }}
            >
              Session ended by administrator
            </Typography>
          )}

          {fields?.Summary?.value && (
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: '#616161',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              <Text field={fields.Summary} />
            </Typography>
          )}

          {fields?.Description?.value ? (
            <Box
              sx={{
                backgroundColor: '#fff8e1',
                borderRadius: 2,
                p: 3,
                mb: 4,
                textAlign: 'left',
                border: '1px solid #ffe082',
                '& h2': {
                  color: '#424242',
                  mb: 2,
                  fontWeight: 600,
                  fontSize: '1rem',
                  margin: 0,
                  marginBottom: '16px',
                },
                '& ul': {
                  pl: 3,
                  color: '#616161',
                  margin: 0,
                  '& li': {
                    mb: 1.5,
                    lineHeight: 1.8,
                  },
                },
              }}
            >
              <RichText field={fields.Description} />
            </Box>
          ) : null}

          {fields?.CTAButton?.value?.href && (
            <Button
              variant="contained"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{
                px: 4,
                py: 1,
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: '#255E5E',
                '&:hover': {
                  backgroundColor: '#1a4a4a',
                },
              }}
            >
              {fields.CTAButton.value.text}
            </Button>
          )}

          {fields?.InfoText?.value ? (
            <Typography
              variant="body2"
              sx={{
                color: '#9e9e9e',
                mt: 4,
                fontStyle: 'italic',
              }}
            >
              <Text field={fields.InfoText} />
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#9e9e9e',
                mt: 4,
                fontStyle: 'italic',
              }}
            >
              Thank you for using the Lead Management App.
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default SessionEndedByAdmin;
